import { useEffect, useMemo, useState } from 'react';
import { trpc } from '../lib/trpc';
import { useAsync } from '../lib/useAsync';
import { vizinhas, progressoPct, progressoModulo, primeiraAula } from '../lib/tutoriaisNav';

/**
 * Tutoriais — onboarding do operador. Renderiza DENTRO do Shell (a sidebar da
 * plataforma continua): a área de conteúdo traz a aula (vídeo + abas) à esquerda
 * e o painel "Conteúdo do curso" (a lista de aulas) à direita, fiel ao design.
 * Estrutura portada do AMA Flow, no design system da AMA Capital (Fraunces +
 * Inter, esmeralda). Currículo vem do back; progresso é por incorporadora.
 */

type Curriculo = Awaited<ReturnType<typeof trpc.tutoriais.curriculo.query>>;
type Modulo = Curriculo['modulos'][number];
type Aula = Modulo['aulas'][number];

const VIDEO_BG = 'linear-gradient(150deg,#12321F,#0A1B12)';

function segDe(mmss?: string): number {
  if (!mmss) return 0;
  const [m, s] = mmss.split(':').map(Number);
  return (m || 0) * 60 + (s || 0);
}
function minLabel(mmss?: string): string {
  return `${Math.max(1, Math.round(segDe(mmss) / 60))} min`;
}

export function TutoriaisPage() {
  const { data, loading, error } = useAsync(() => trpc.tutoriais.curriculo.query());
  if (loading) return <p className="py-16 text-center text-sm text-ink-3">Carregando…</p>;
  if (error || !data)
    return <p className="py-16 text-center text-sm text-ink-3">Não foi possível carregar os tutoriais.</p>;
  return <Conteudo data={data} />;
}

function Conteudo({ data }: { data: Curriculo }) {
  const { modulos } = data;

  const [concluidas, setConcluidas] = useState<Set<string>>(() => new Set(data.concluidas));
  const [aulaId, setAulaId] = useState<string | null>(() => primeiraAula(modulos));
  const [abertos, setAbertos] = useState<Set<string>>(() => new Set(modulos.map((m) => m.id)));
  const [aba, setAba] = useState<'sobre' | 'materiais' | 'perguntas'>('sobre');
  const [salvando, setSalvando] = useState(false);

  const flat = useMemo(() => modulos.flatMap((m) => m.aulas.map((a) => ({ a, mod: m }))), [modulos]);
  const indice = useMemo(() => new Map(flat.map((x, i) => [x.a.id, { ...x, i }])), [flat]);
  const totalAulas = data.totalAulas;
  const totalMin = useMemo(
    () => Math.round(flat.reduce((s, x) => s + segDe(x.a.duracao), 0) / 60),
    [flat],
  );

  const atual = aulaId ? indice.get(aulaId) : undefined;
  const { anterior, proxima } = aulaId ? vizinhas(modulos, aulaId) : { anterior: null, proxima: null };
  const pct = progressoPct(totalAulas, concluidas.size);
  const isDone = aulaId ? concluidas.has(aulaId) : false;

  useEffect(() => {
    if (!atual) return;
    setAbertos((p) => (p.has(atual.mod.id) ? p : new Set(p).add(atual.mod.id)));
    setAba('sobre');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [aulaId]);

  function toggleModulo(id: string) {
    setAbertos((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function marcar(v: boolean) {
    if (!aulaId) return;
    const aplicar = (add: boolean) =>
      setConcluidas((p) => {
        const n = new Set(p);
        if (add) n.add(aulaId);
        else n.delete(aulaId);
        return n;
      });
    aplicar(v);
    setSalvando(true);
    try {
      await trpc.tutoriais.marcar.mutate({ aulaId, concluida: v });
    } catch {
      aplicar(!v);
    } finally {
      setSalvando(false);
    }
  }

  return (
    // Largura CHEIA: o painel "Conteúdo do curso" fica colado na borda DIREITA
    // da tela (2ª coluna do grid). Quem tem largura limitada é só a coluna do
    // vídeo (max-w no <main>), para o player 16:9 não esticar em telas largas —
    // como na referência (vídeo à esquerda, lista de aulas na direita).
    <div data-testid="tutoriais-page">
      {/* Faixa de progresso (topo do conteúdo, à direita — como no design). */}
      <div className="mb-5 flex items-center justify-end gap-3" data-testid="tutoriais-progresso-topo">
        <span className="hidden text-[13px] text-ink-3 sm:inline">
          {concluidas.size} de {totalAulas} aulas
        </span>
        <div className="h-1.5 w-40 overflow-hidden rounded-full bg-line">
          <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="num text-[13px] font-semibold text-ink">{pct}%</span>
      </div>

      <div className="flex flex-col gap-8 xl:flex-row">
        {/* Área principal (vídeo/abas) — FICA ONDE ESTÁ: cresce para ocupar o
            espaço à esquerda do painel e centraliza o player na sua área (não
            joga para a esquerda). Só o painel do curso é que vai para a direita. */}
        <div className="min-w-0 flex-1">
          <main className="mx-auto w-full max-w-[860px]" data-testid="tutoriais-centro">
            {atual ? (
              <AulaAtual
                atual={atual}
                total={totalAulas}
                isDone={isDone}
                salvando={salvando}
                onMarcar={marcar}
                onAnterior={() => anterior && setAulaId(anterior)}
                onProxima={() => proxima && setAulaId(proxima)}
                temAnterior={!!anterior}
                temProxima={!!proxima}
                aba={aba}
                setAba={setAba}
                proximaAula={proxima ? indice.get(proxima)?.a ?? null : null}
                onIrProxima={() => proxima && setAulaId(proxima)}
              />
            ) : (
              <p className="text-sm text-ink-3">Selecione uma aula.</p>
            )}
          </main>
        </div>

        {/* Painel do curso — colado na borda DIREITA da tela. */}
        <aside className="shrink-0 xl:w-[340px]">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-[15px] font-semibold text-ink">Conteúdo do curso</h2>
            <span className="text-[12px] text-ink-3">
              {totalAulas} aulas · {totalMin} min
            </span>
          </div>
          <nav className="space-y-1.5" aria-label="Módulos">
            {modulos.map((mod) => (
              <ModuloItem
                key={mod.id}
                mod={mod}
                aberto={abertos.has(mod.id)}
                onToggle={() => toggleModulo(mod.id)}
                concluidas={concluidas}
                aulaAtiva={aulaId}
                onSelecionar={setAulaId}
              />
            ))}
          </nav>

          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-line bg-surface p-4">
            <span
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
              style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                <path d="M4 6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H9l-4 3V6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              </svg>
            </span>
            <div>
              <div className="text-[13.5px] font-semibold text-ink">Ficou com dúvida?</div>
              <div className="text-[12.5px] text-ink-3">Fale com o suporte da AMA Capital.</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function AulaAtual({
  atual,
  total,
  isDone,
  salvando,
  onMarcar,
  onAnterior,
  onProxima,
  temAnterior,
  temProxima,
  aba,
  setAba,
  proximaAula,
  onIrProxima,
}: {
  atual: { a: Aula; mod: Modulo; i: number };
  total: number;
  isDone: boolean;
  salvando: boolean;
  onMarcar: (v: boolean) => void;
  onAnterior: () => void;
  onProxima: () => void;
  temAnterior: boolean;
  temProxima: boolean;
  aba: 'sobre' | 'materiais' | 'perguntas';
  setAba: (a: 'sobre' | 'materiais' | 'perguntas') => void;
  proximaAula: Aula | null;
  onIrProxima: () => void;
}) {
  const { a, mod, i } = atual;
  return (
    <>
      <h1 className="font-serif text-[32px] font-medium leading-tight tracking-tight text-ink">{a.titulo}</h1>
      <p className="mt-1 text-[14px] text-ink-3">
        {a.subtitulo ? `${a.subtitulo} · ` : ''}módulo {mod.titulo}
      </p>

      {/* Player */}
      <div className="mt-5 overflow-hidden rounded-[18px]" style={{ boxShadow: 'var(--shadow-sm)' }}>
        <div className="relative aspect-video w-full" style={{ background: VIDEO_BG }}>
          {a.videoUrl ? (
            <iframe
              src={a.videoUrl}
              title={a.titulo}
              className="absolute inset-0 h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <>
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
                <span
                  className="flex h-[74px] w-[74px] items-center justify-center rounded-full"
                  style={{ background: 'var(--accent)', boxShadow: '0 12px 30px -8px rgba(0,0,0,.5)' }}
                >
                  <svg viewBox="0 0 24 24" width="30" height="30" fill="white" aria-hidden="true">
                    <path d="M9 7.5l8 4.5-8 4.5v-9z" />
                  </svg>
                </span>
                <div className="font-serif text-[22px] font-medium text-white">{a.titulo}</div>
                <div className="text-[12.5px] text-white/70">
                  Aula {i + 1} de {total} · {minLabel(a.duracao)} · o vídeo desta aula chega em breve
                </div>
              </div>
              {/* Barra de controles (decorativa enquanto o vídeo não chega) */}
              <div
                className="absolute inset-x-0 bottom-0 flex items-center gap-3 px-4 py-3"
                style={{ background: 'linear-gradient(transparent, rgba(0,0,0,.55))' }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="white" aria-hidden="true">
                  <path d="M9 7.5l8 4.5-8 4.5v-9z" />
                </svg>
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/25">
                  <div className="h-full w-0 rounded-full bg-white/80" />
                </div>
                <span className="num text-[12px] text-white/85">0:00 / {a.duracao ?? '0:00'}</span>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="white" strokeWidth="1.8" aria-hidden="true">
                  <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Navegação + marcar */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button type="button" onClick={onAnterior} disabled={!temAnterior} className="btn btn-ghost h-11 px-4 text-[13px] disabled:opacity-40">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Anterior
          </button>
          <button type="button" onClick={onProxima} disabled={!temProxima} data-testid="tutoriais-proxima" className="btn btn-ghost h-11 px-4 text-[13px] disabled:opacity-40">
            Próxima
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
        <button
          type="button"
          onClick={() => onMarcar(!isDone)}
          disabled={salvando}
          data-testid="tutoriais-marcar"
          className={`btn h-11 px-5 text-[14px] disabled:opacity-60 ${isDone ? 'btn-ghost' : 'btn-primary'}`}
          style={isDone ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : undefined}
        >
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 12.5l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          {isDone ? 'Concluída' : 'Marcar como concluída'}
        </button>
      </div>

      {/* Abas */}
      <div className="mt-6 flex gap-6 border-b border-line">
        {([['sobre', 'Sobre esta aula'], ['materiais', 'Materiais'], ['perguntas', 'Perguntas']] as const).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setAba(k)}
            className={`-mb-px border-b-2 pb-3 text-[14px] transition-colors ${
              aba === k ? 'border-accent font-semibold text-ink' : 'border-transparent text-ink-3 hover:text-ink-2'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {aba === 'sobre' && (
        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <p className="text-[15px] leading-relaxed text-ink-2">{a.descricao ?? 'Descrição não disponível'}</p>
            {a.topicos && a.topicos.length > 0 && (
              <>
                <div className="mt-5 text-[13px] font-semibold text-ink">O que você vai aprender</div>
                <ul className="mt-2 space-y-2">
                  {a.topicos.map((t) => (
                    <li key={t} className="flex items-start gap-2 text-[14px] text-ink-2">
                      <svg viewBox="0 0 24 24" width="17" height="17" className="mt-0.5 shrink-0 text-accent" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12.5l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      {t}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <div className="space-y-3">
            <MateriaisCard />
            {proximaAula && (
              <div className="rounded-2xl p-4" style={{ background: 'var(--accent-soft)' }} data-testid="tutoriais-proximo-passo">
                <div className="text-[12.5px] font-semibold" style={{ color: 'var(--accent-deep)' }}>Próximo passo sugerido</div>
                <div className="mt-1 text-[14px] font-medium text-ink">{proximaAula.titulo}</div>
                <button type="button" onClick={onIrProxima} className="mt-2 text-[13px] font-semibold" style={{ color: 'var(--accent)' }}>
                  Ir para a próxima aula →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {aba === 'materiais' && (
        <div className="mt-5 max-w-md">
          <MateriaisCard />
        </div>
      )}

      {aba === 'perguntas' && (
        <div className="mt-6 rounded-2xl border border-line bg-surface p-6 text-center">
          <div className="text-[14px] font-medium text-ink">As perguntas desta aula chegam em breve</div>
          <div className="mt-1 text-[13px] text-ink-3">Enquanto isso, fale com o suporte da AMA Capital para tirar dúvidas.</div>
        </div>
      )}
    </>
  );
}

/** Materiais de apoio da aula (em preparação — placeholders, como os vídeos). */
function MateriaisCard() {
  const itens = [
    { nome: 'Mapa da plataforma', tipo: 'PDF' },
    { nome: 'Planilha modelo de contratos', tipo: 'XLSX' },
  ];
  return (
    <div className="rounded-2xl border border-line bg-surface p-4">
      <div className="text-[13px] font-semibold text-ink">Materiais desta aula</div>
      <ul className="mt-3 space-y-2.5">
        {itens.map((it) => (
          <li key={it.nome} className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: 'var(--surface-2)', color: 'var(--ink-3)' }} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M7 3h8l4 4v14a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" strokeLinejoin="round" /><path d="M14 3v5h5" /></svg>
            </span>
            <div className="min-w-0">
              <div className="truncate text-[13.5px] font-medium text-ink">{it.nome}</div>
              <div className="text-[12px] text-ink-4">{it.tipo} · em breve</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ModuloItem({
  mod,
  aberto,
  onToggle,
  concluidas,
  aulaAtiva,
  onSelecionar,
}: {
  mod: Modulo;
  aberto: boolean;
  onToggle: () => void;
  concluidas: Set<string>;
  aulaAtiva: string | null;
  onSelecionar: (id: string) => void;
}) {
  const { feitas, total } = progressoModulo(mod, concluidas);
  const completo = feitas === total;
  return (
    <div>
      <button type="button" onClick={onToggle} aria-expanded={aberto} className="flex w-full items-center gap-2 py-2 text-left">
        <svg viewBox="0 0 24 24" width="15" height="15" className={`shrink-0 text-ink-4 transition-transform ${aberto ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        <span className="flex-1 truncate text-[14px] font-semibold text-ink">{mod.titulo}</span>
        <span className="num text-[12px] font-semibold" style={{ color: completo ? 'var(--accent)' : 'var(--ink-3)' }}>
          {feitas}/{total}
        </span>
      </button>
      {aberto && (
        <ul className="mb-1 space-y-0.5 pl-1">
          {mod.aulas.map((a) => {
            const done = concluidas.has(a.id);
            const ativa = a.id === aulaAtiva;
            return (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => onSelecionar(a.id)}
                  aria-current={ativa}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left ${ativa ? 'bg-accent-soft' : 'hover:bg-surface-2'}`}
                >
                  {done ? (
                    <svg viewBox="0 0 24 24" width="17" height="17" className="shrink-0 text-accent" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="currentColor" /><path d="M8 12.5l2.5 2.5L16 9.5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="17" height="17" className="shrink-0 text-ink-4" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" fill="none" /></svg>
                  )}
                  <span className={`flex-1 truncate text-[13.5px] ${ativa ? 'font-medium text-accent' : 'text-ink-2'}`}>{a.titulo}</span>
                  <span className="num text-[12px] text-ink-4">{a.duracao}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
